// @ts-nocheck

import React, { useState } from 'react';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import useDebouncedValue from 'react-scale/hooks/useDebouncedValue';
import Autocomplete from './SimpleAutocomplete';
import HTTPError from '@scale/frontend-lib/httpError';
import { AutocompleteOption } from 'design-system/Autocomplete/Autocomplete';
import { CourseVersion } from '@scale/shared/defaults';
import { useQuery } from 'react-query';

type CourseAutocompleteOption = AutocompleteOption & {
  courseVersion: CourseVersion;
};

const queryKey = 'fetch-courses'; // Arbitrary token to disambiguate lookups to useQuery.
async function searchCourses(text: string | undefined): Promise<CourseAutocompleteOption[]> {
  try {
    return await apiFetch(null, `corp-api/courses/autocomplete/optimized`, {
      query: {
        search: text,
      },
    }).then(
      ({ results }) =>
        results.map(
          ({
            id,
            name,
            courseVersion,
          }: {
            id: string;
            name: string;
            courseVersion: CourseVersion;
          }) => ({
            id,
            title: name,
            courseVersion,
          }),
        ) as CourseAutocompleteOption[],
    );
  } catch (error) {
    defaultApiFetchErrorHandler(error as HTTPError, { handleHttp: true });
    return [];
  }
}
const CourseAutocomplete: React.FC<{
  selectedCourse: { id: string; title: string; courseVersion?: CourseVersion };
  setSelectedCourse: (value: { id: string; title: string; courseVersion?: CourseVersion }) => void;
  disabled?: boolean;
}> = ({ selectedCourse, setSelectedCourse, disabled }) => {
  const [inputValue, setInputValue] = useState<string>('');
  const debouncedInputValue = useDebouncedValue(inputValue, 200);
  const queryResults = useQuery<CourseAutocompleteOption[], Error>(
    [queryKey, debouncedInputValue, selectedCourse.id],
    async () => {
      const inputSearchResults = await searchCourses(debouncedInputValue);
      const pinnedSearchResults = await searchCourses(selectedCourse.id);
      return inputSearchResults.concat(pinnedSearchResults);
    },
  );
  const options = queryResults.data ?? [];

  return (
    <>
      <Autocomplete
        id="course-autocomplete"
        className="w-full"
        options={options}
        value={{
          ...selectedCourse,
          title:
            selectedCourse.title && selectedCourse.title.length
              ? selectedCourse.title
              : options.find(option => option.id === selectedCourse.id)?.title ?? '',
        }}
        setSelectedOption={setSelectedCourse}
        onInputChange={setInputValue}
        disabled={disabled}
        loading={queryResults.status === 'loading'}
        placeholder="Search course by name..."
      />
    </>
  );
};

export default CourseAutocomplete;
